import axios from "axios";

import { getErrorMessage } from "~/v2/api/hooks/errorParsing";
import type { TravelRuleValidationError } from "~/v2/api/rest/cryptoWallet";
import { formatErrorMessage } from "./presentation/RequestedFieldsPanel";
import {
  type AddressMatchModalState,
  collectForcedFields,
  normalizeRequest,
  parseVaspMatch,
  type TravelRuleRequest,
} from "./travelRuleExtraFields";

// Returns the cryptowallet validation payload (`{ error_code, message,
// next_actions: [...] }`) or null for any other error shape (network, 5xx,
// generic 4xx) — those route to a generic toast.
function parseTravelRuleValidationError(
  error: Error,
): TravelRuleValidationError | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }
  const data = error.response?.data;
  if (data?.error_code && Array.isArray(data?.next_actions)) {
    return data as TravelRuleValidationError;
  }
  return null;
}

// Deps are injected so this can be unit-tested without the full hook tree.
export interface HandleMutationErrorDeps {
  /** Open the locked-design VASP-match modal for explicit user acceptance. */
  setAddressMatchModal: (modal: AddressMatchModalState | null) => void;
  /** Render banner + requested fields in the unified RequestedFieldsPanel. */
  setRequest: (request: TravelRuleRequest | null) => void;
  /** Fallback for non-validation errors and validation errors with nothing actionable. */
  toastError: (message: string) => void;
}

/**
 * Route a cryptowallet whitelist mutation error to UI side-effects:
 *   - non-validation (network, 5xx, ...) -> toast
 *   - `ADDRESS_BELONGS_TO_VASP` -> AddressMatchModal (locked accept/cancel UX)
 *   - other validation w/ usable `next_actions` -> RequestedFieldsPanel
 *   - validation that filters to nothing actionable -> toast
 */
export function handleTravelRuleMutationError(
  error: Error,
  deps: HandleMutationErrorDeps,
): void {
  const validationError = parseTravelRuleValidationError(error);
  if (!validationError) {
    deps.toastError(`Failed: ${getErrorMessage(error)}`);
    return;
  }

  if (validationError.error_code === "ADDRESS_BELONGS_TO_VASP") {
    const forcedFields = collectForcedFields(validationError);
    deps.setAddressMatchModal({
      forcedFields,
      ...parseVaspMatch(validationError, forcedFields),
    });
    return;
  }

  const fields = normalizeRequest(validationError);
  if (fields.length === 0) {
    deps.toastError(validationError.message || "Validation failed");
    return;
  }

  deps.setRequest({
    banner: formatErrorMessage(validationError.message),
    fields,
  });
}
